import {Action, Selector, State, StateContext} from '@ngxs/store';
import {Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import {map, tap} from 'rxjs/operators';
import {Auth} from '../actions/auth.actions';
import {Subscription} from '../models/subscription/subscription.model';
import {Subscriptions} from '../actions/subscription.actions';
import ClearState = Auth.ClearState;
import GetSubscription = Subscriptions.GetSubscription;
import GetSubscriptions = Subscriptions.GetSubscriptions;
import CreateSubscription = Subscriptions.CreateSubscription;
import UpdateSubscription = Subscriptions.UpdateSubscription;
import DeleteSubscription = Subscriptions.DeleteSubscription;
import {SubscriptionService} from '../services/subscription.service';

export interface SubscriptionStateModel {
  subscriptions: Array<Subscription>;
  currentSubscription: Subscription;
}

@State<SubscriptionStateModel>({
  name: 'subscription',
  defaults: {
    subscriptions: [],
    currentSubscription: null,
  }
})
@Injectable()
export class SubscriptionState {
  constructor(private subscriptionService: SubscriptionService) {
  }

  @Selector()
  static subscriptions(state: SubscriptionStateModel): Array<Subscription> {
    return state.subscriptions;
  }

  @Selector()
  static subscription(state: SubscriptionStateModel): Subscription {
    return state.currentSubscription;
  }


  @Action(GetSubscriptions)
  getSubscriptions(ctx: StateContext<SubscriptionStateModel>, action: GetSubscriptions): Observable<Subscription[]> {
    if (!action.force) {
      const subscriptions = ctx.getState().subscriptions;
      if (subscriptions.length > 0) {
        return of(subscriptions);
      }
    }
    return this.subscriptionService.getSubscriptions().pipe(
      tap(res => {
        ctx.patchState({
          subscriptions: res.subscriptions,
        });
      }),
      map(res => res.subscriptions)
    );
  }

  @Action(GetSubscription)
  getSubscription(ctx: StateContext<SubscriptionStateModel>, action: GetSubscription): Observable<Subscription> {
    if (!action.force) {
      const subscription = ctx.getState().currentSubscription;
      if (subscription && subscription.id === action.id) {
        return of(subscription);
      }

      const cachedSubscription = ctx.getState().subscriptions.find(p => p.id === action.id);
      if (cachedSubscription) {
        ctx.patchState({
          currentSubscription: cachedSubscription
        });
        return of(cachedSubscription);
      }
    }
    return this.subscriptionService.getSubscription(action.id).pipe(
      tap(subscription => {
        ctx.patchState({
          currentSubscription: subscription
        });
      })
    );
  }

  @Action(CreateSubscription)
  createSubscription(ctx: StateContext<SubscriptionStateModel>, action: CreateSubscription): Observable<Subscription> {
    return this.subscriptionService.createSubscription(action.subscription).pipe(
      tap(subscription => {
        ctx.patchState({
          currentSubscription: subscription
        });
      })
    );
  }

  @Action(UpdateSubscription)
  updateSubscription(ctx: StateContext<SubscriptionStateModel>, action: UpdateSubscription): Observable<Subscription> {
    return this.subscriptionService.updateSubscription(action.subscription).pipe(
      tap(subscription => {
        ctx.patchState({
          currentSubscription: subscription
        });
      })
    );
  }

  @Action(DeleteSubscription)
  deleteSubscription(ctx: StateContext<SubscriptionStateModel>, action: DeleteSubscription): Observable<Subscription> {
    const subscriptions = ctx.getState().subscriptions;
    const index = subscriptions.indexOf(action.subscription);
    return this.subscriptionService.deleteSubscription(action.subscription.id).pipe(
      tap(subscription => {
        ctx.patchState({
          currentSubscription: null,
          subscriptions: [
            ...subscriptions.slice(0, index),
            ...index === subscriptions.length - 1 ? [] : subscriptions.slice(index + 1)
          ]
        });
      })
    );
  }

  @Action(ClearState)
  clearState(ctx: StateContext<SubscriptionStateModel>): void {
    ctx.setState({
      subscriptions: [],
      currentSubscription: null,
    });
  }
}
